import {
  KebabHorizontalIcon,
  PencilIcon,
  PlusIcon,
  SyncIcon,
  TrashIcon
} from "@primer/octicons-react";
import {
  ActionList,
  ActionMenu,
  Box,
  IconButton,
  useConfirm,
  Text as PrimerText,
  Dialog
} from "@primer/react";
import { useCallback, useEffect, useState } from "react";
import Button from "../../components/ui/button";
import DataTable from "../../components/ui/data-table";
import TextInput from "../../components/ui/text-input";
import Text from "../../components/ui/text";
import { useCommonContext } from "../../contexts/common";
import { NotifyHelper } from "../../helpers/toast";

import moment from "moment";
import { lichSuXuongPhongYTeApi } from "../../api/lichSuXuongPhongYTeApi";
import { danThuocApi } from "../../api/danThuocApi";
import { useCommonSelectedHook } from "../../hooks/useCommonSelectedHook";
import { ILichSuXuongPhongYTe } from "../../models/response/crm-hocsinh/ILichSuXuongPhongYTe";
import { IFaDanThuocItemRepsone } from "../../models/response/dan-thuoc/IDanThuocItemRespone";
import LichSuXuongPhongYTeModal from "./LichSuXuongPhongYTeModal";
import TextTranslated from "../../components/text";
import HocSinhProfile from "../hoc-sinh-profile";

import TextWithEllipse from "../../components/ui/text-with-ellipse";
import HocSinhShortInfo from "../../components/hoc-sinh/HocSinhShortInfo";
import HocSinhShortInfoWithClass from "../../components/hoc-sinh/HocSinhShortInfoWithClass";

const LichSuXuongPhongYTePage = () => {
  const [isShowModal, setIsShowModal] = useState(false);
  const [selectedId, setSelectedId] = useState(0);
  const [lichSuXuongPhongYTes, setLichSuXuongPhongYTes] = useState<ILichSuXuongPhongYTe[]>([]);
  const { translate } = useCommonContext();
  const confirm = useConfirm();
  const [isLoading, setIsLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const { nam_hoc, dm_coso_id } = useCommonSelectedHook();

  // Filter states for dặn thuốc - chỉ lấy ngày hiện tại
  const [danThuocData, setDanThuocData] = useState<(IFaDanThuocItemRepsone & { isConfirmed?: boolean })[]>([]);
  const [isDanThuocLoading, setIsDanThuocLoading] = useState(false);

  // State cho modal dặn thuốc
  const [isShowDanThuocModal, setIsShowDanThuocModal] = useState(false);
  const [selectedDanThuocHocSinh, setSelectedDanThuocHocSinh] = useState<IFaDanThuocItemRepsone | null>(null);

  // State cho modal HocSinhProfile
  const [isShowHocSinhProfile, setIsShowHocSinhProfile] = useState(false);
  const [hocSinhEditing, setHocSinhEditing] = useState<{ id: number }>({ id: 0 });
  const canEdit = true; // Có thể điều chỉnh theo quyền

  // Filter states cho lịch sử y tế
  const [filterTuNgay, setFilterTuNgay] = useState<string>(moment().format('YYYY-MM-DD'));
  const [filterDenNgay, setFilterDenNgay] = useState<string>(moment().format('YYYY-MM-DD'));

  const handleReloadAsync = useCallback(async () => {

    try {
      setIsLoading(true);
      const res = await lichSuXuongPhongYTeApi.selectAll(dm_coso_id, nam_hoc);
      if (res.is_success) {
        // Filter dữ liệu theo ngày nếu có filter
        let filteredData = res.data;
        if (filterTuNgay || filterDenNgay) {
          filteredData = res.data.filter((item: any) => {
            const ngayTao = moment(item.ngay_tao).format('YYYY-MM-DD');
            const tuNgay = filterTuNgay || '1900-01-01';
            const denNgay = filterDenNgay || '2100-12-31';
            return ngayTao >= tuNgay && ngayTao <= denNgay;
          });
        }
        setLichSuXuongPhongYTes(filteredData);
      } else {
        NotifyHelper.Error(res.message ?? "");
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi xảy ra khi tải dữ liệu");
    } finally {
      setIsLoading(false);
    }
  }, [nam_hoc, dm_coso_id, filterTuNgay, filterDenNgay]);

  const handleReloadDanThuocAsync = useCallback(async () => {
    try {
      setIsDanThuocLoading(true);
      const today = moment().format('YYYY-MM-DD');
      const res = await danThuocApi.selectByCoso({
        tu_ngay: today,
        den_ngay: today,
        dm_coso_id: dm_coso_id,
        nam_hoc: nam_hoc
      });
      if (res.is_success) {
        // Lọc chỉ những học sinh có lịch uống thuốc trong ngày hiện tại
        const filteredData = res.data.filter((item: IFaDanThuocItemRepsone) => {
          const tuNgay = moment(item.tu_ngay);
          const denNgay = moment(item.den_ngay);
          const currentDate = moment();
          return currentDate.isBetween(tuNgay, denNgay, 'day', '[]');
        });

        // Thêm thông tin đã xác nhận hay chưa
        const dataWithConfirmStatus = filteredData.map((item: IFaDanThuocItemRepsone) => ({
          ...item,
          isConfirmed: checkIfStudentConfirmed(item.ts_hocsinh_id, item.id)
        }));

        setDanThuocData(dataWithConfirmStatus);
      } else {
        NotifyHelper.Error(res.message ?? "Có lỗi khi tải dữ liệu dặn thuốc");
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi xảy ra khi tải dữ liệu dặn thuốc");
    } finally {
      setIsDanThuocLoading(false);
    }
  }, [dm_coso_id, nam_hoc, lichSuXuongPhongYTes]);

  // Kiểm tra xem dặn thuốc này đã được xác nhận chưa
  const checkIfStudentConfirmed = (ts_hocsinh_id: number, fa_danthuoc_id?: number): boolean => {
    return lichSuXuongPhongYTes.some((lichSu: ILichSuXuongPhongYTe) => {
      const isDanThuocType = lichSu.nhom_noi_dung === "Dặn thuốc";
      const isSameStudent = lichSu.ts_hocsinh_id === ts_hocsinh_id;

      // Nếu có fa_danthuoc_id, kiểm tra chính xác theo ID dặn thuốc
      if (fa_danthuoc_id && lichSu.fa_danthuoc_id) {
        return isDanThuocType && isSameStudent && lichSu.fa_danthuoc_id === fa_danthuoc_id;
      }

      // Fallback: kiểm tra theo ngày (cho các bản ghi cũ chưa có fa_danthuoc_id)
      const today = moment().format('YYYY-MM-DD');
      const isToday = moment(lichSu.ngay_tao).format('YYYY-MM-DD') === today;
      return isToday && isDanThuocType && isSameStudent;
    });
  };

  useEffect(() => {
    handleReloadAsync();
  }, [nam_hoc, dm_coso_id, handleReloadAsync]);

  useEffect(() => {
    handleReloadDanThuocAsync();
  }, [dm_coso_id, nam_hoc, handleReloadDanThuocAsync]);

  // Reload dữ liệu dặn thuốc khi lịch sử y tế thay đổi để cập nhật trạng thái xác nhận
  useEffect(() => {
    if (danThuocData.length > 0) {
      handleReloadDanThuocAsync();
    }
  }, [lichSuXuongPhongYTes]);

  const handeDelete = async (id: number) => {
    if (
      await confirm({
        content: "Bạn có chắc chắn muốn xóa?",
        title: "Lưu ý",
        cancelButtonContent: "Không xóa",
        confirmButtonContent: "Xóa",
        confirmButtonType: "danger",
      })
    ) {
      try {
        const res = await lichSuXuongPhongYTeApi.delete(id);
        if (res.is_success) {
          NotifyHelper.Success("Success");
          handleReloadAsync();
        } else {
          NotifyHelper.Error(res.message ?? "");
        }
      } catch (error) {
        NotifyHelper.Error("Có lỗi xảy ra khi xóa");
      }
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", gap: 2, height: `${window.innerHeight - 210}px` }}>
        {/* Bảng lịch sử xuống phòng y tế - bên trái */}
        <Box sx={{ width: "65%" }}>
          <DataTable
            fixedLeftColumns={3}
            height={`${window.innerHeight - 220}px`}
            paging={{
              enable: true,
              pageSizeItems: [10, 20, 50, 100],
            }}
            actionComponent={
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "flex-end",
                  flexWrap: "nowrap"
                }}
              >
                {/* Date filters */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, marginRight: "5px" }}>
                  <Text text="Từ ngày" sx={{ minWidth: "50px", fontSize: "13px", marginRight: "5px" }} />
                  <TextInput
                    type="date"
                    value={filterTuNgay}
                    onChange={(e) => setFilterTuNgay(e.target.value)}
                    sx={{ width: "120px" }}
                  />
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, marginRight: "5px" }}>
                  <Text text="Đến ngày" sx={{ minWidth: "55px", fontSize: "13px", marginRight: "5px" }} />
                  <TextInput
                    type="date"
                    value={filterDenNgay}
                    onChange={(e) => setFilterDenNgay(e.target.value)}
                    sx={{ width: "120px" }}
                  />
                </Box>

                <Button
                  text="Base.Label.Refresh"
                  leadingVisual={SyncIcon}
                  size="small"
                  onClick={handleReloadAsync}
                  disabled={isLoading}
                />
                <Button
                  text={translate("Base.Label.Create")}
                  leadingVisual={PlusIcon}
                  variant="primary"
                  size="small"
                  onClick={() => {
                    setIsShowModal(true);
                    setSelectedId(0);
                  }}
                  disabled={isLoading}
                />
              </Box>
            }
            columns={[
              {
                id: "cmd",
                width: "50px",
                caption: "#",
                align: "center",
                cellRender: (data: ILichSuXuongPhongYTe) => {
                  return (
                    <ActionMenu>
                      <ActionMenu.Anchor>
                        <IconButton
                          icon={KebabHorizontalIcon}
                          variant="invisible"
                          aria-label="Open column options"
                        />
                      </ActionMenu.Anchor>

                      <ActionMenu.Overlay>
                        <ActionList>

                          <ActionList.Item
                            onSelect={() => {
                              setIsShowModal(true);
                              if (data.id !== undefined) {
                                setSelectedId(data.id);
                              }
                            }}
                          >
                            <ActionList.LeadingVisual>
                              <PencilIcon />
                            </ActionList.LeadingVisual>
                            <TextTranslated value="Base.Label.Edit" />
                          </ActionList.Item>

                          <ActionList.Item
                            variant="danger"
                            onSelect={() => {
                              if (data.id !== undefined) {
                                handeDelete(data.id);
                              }
                            }}
                          >
                            <ActionList.LeadingVisual>
                              <TrashIcon />
                            </ActionList.LeadingVisual>
                            <TextTranslated value="Base.Label.Delete" />
                          </ActionList.Item>
                        </ActionList>
                      </ActionMenu.Overlay>
                    </ActionMenu>
                  );
                },
              },
              {
                dataField: "ma_hs",
                width: 80,
                align: "center",
                caption: "Mã HS",
                fixed: true
              },
              {
                id: "ho_ten",
                caption: "Họ tên",
                width: 180,
                fixed: true,
                cellRender: (data: ILichSuXuongPhongYTe) => (
                  <Box
                    onClick={() => {
                      setHocSinhEditing({ id: data.ts_hocsinh_id });
                      setIsShowHocSinhProfile(true);
                    }}
                    sx={{ cursor: "pointer" }}
                  >
                    <HocSinhShortInfo
                      data={data}
                    />
                  </Box>
                ),
              },
              {
                id: "thong_tin_truong_lop",
                caption: "Lớp - Hệ",
                width: 120,
                cellRender: (data: ILichSuXuongPhongYTe) => {
                  return (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "baseline",
                      }}
                    >
                      <Box sx={{ display: "flex" }}>
                        <Box sx={{ fontWeight: 600, fontSize: "12px" }}>{data.ten_lop}</Box>
                      </Box>
                      <Box sx={{ color: "fg.muted", fontSize: "11px" }}>
                        Hệ {data.ten_he}
                      </Box>
                    </Box>
                  );
                },
              },
              {
                id: "hinh_anh",
                width: "100px",
                caption: `Hình ảnh`,
                cellRender: (data: ILichSuXuongPhongYTe) => {
                  const images = data.images || [];
                  return (
                    <Box sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: "50px",
                      height: "100%",
                      gap: 1
                    }}>
                      {images.filter((imageUrl: string) => imageUrl).map((imageUrl: string, idx: number) => {
                        return (
                          <Box key={idx}>
                            <Box
                              onClick={() => setPreviewImage(imageUrl)}
                              sx={{ cursor: "pointer" }}
                            >
                              <img src={imageUrl} alt='Image'
                                style={{
                                  height: "40px",
                                  width: "auto",
                                  borderRadius: "2px"
                                }}
                              />
                            </Box>
                          </Box>
                        );
                      })}
                      {images.length === 0 && (
                        <Box sx={{ color: "fg.muted", fontSize: 0 }}>
                          Không có ảnh
                        </Box>
                      )}
                    </Box>
                  );
                }
              },

              {
                dataField: "nhom_noi_dung",
                width: "120px",
                caption: `Loại`,
                cellRender: (data: ILichSuXuongPhongYTe) => {
                  return (
                    <Box sx={{ fontSize: "12px" }}>
                      <TextWithEllipse text={data.nhom_noi_dung || "-"} lineNumber={2} />
                    </Box>
                  );
                },
              },
              {
                dataField: "ket_qua",
                width: "200px",
                caption: `Kết quả`,
                cellRender: (data: ILichSuXuongPhongYTe) => {
                  return (
                    <Box
                      sx={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        fontSize: "12px"
                      }}
                    >
                      <TextWithEllipse text={data.ket_qua || ""} lineNumber={2} allowToggleText={true} />
                    </Box>
                  );
                },
              },
              {
                dataField: "ngay_tao",
                width: "100px",
                caption: `Ngày tạo`,
                cellRender: (data: ILichSuXuongPhongYTe) => {
                  return (
                    <Box sx={{ whiteSpace: "nowrap", fontSize: "12px" }}>
                      {moment(data.ngay_tao).format("DD/MM/YY") || ""}
                    </Box>
                  );
                },
              },
            ]}
            data={lichSuXuongPhongYTes} 
            searchEnable
          />
        </Box>

        {/* Bảng lịch dặn thuốc trong ngày - bên phải */}
        <Box sx={{
          width: "35%",
                      height: `${window.innerHeight - 140}px`,

          border: "1px solid",
          borderColor: "border.muted",
          borderRadius: 2,
          backgroundColor: "canvas.default",
          display: "flex",
          flexDirection: "column"
        }}>
          {/* Header */}
          <Box sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            borderBottom: "1px solid",
            borderColor: "border.muted",
            backgroundColor: "canvas.subtle"
          }}>
            <Box>
              <PrimerText sx={{ fontSize: 2, fontWeight: 600, marginRight: 2 }}>
                Dặn thuốc trong ngày
              </PrimerText>
              <Box sx={{
                backgroundColor: "#ff6b35",
                color: "white",
                px: 2,
                py: 0.5,
                borderRadius: "12px",
                fontSize: 1,
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center"
              }}>
                {danThuocData.length}
              </Box>
            </Box>
            <Button
              text="Base.Label.Refresh"
              leadingVisual={SyncIcon}
              size="medium"
              onClick={handleReloadDanThuocAsync}
              disabled={isDanThuocLoading}
            />
          </Box>

          {/* Cards Container */}
          <Box sx={{
            overflowY: "auto",
            p: 2,
            flex: 1
          }}>
            {isDanThuocLoading ? (
              <Box sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px"
              }}>
                <PrimerText sx={{ color: "fg.muted" }}>Đang tải...</PrimerText>
              </Box>
            ) : danThuocData.length === 0 ? (
              <Box sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px"
              }}>
                <PrimerText sx={{ color: "fg.muted" }}>Không có dữ liệu</PrimerText>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {danThuocData.map((data, index) => {
                  const isConfirmed = data.isConfirmed || false;
                  const images = data.hinhAnhs || [];

                  return (
                    <Box key={index} sx={{
                      border: "1px solid",
                      borderColor: isConfirmed ? "success.muted" : "border.muted",
                      borderRadius: 2,
                      p: 2,
                      backgroundColor: isConfirmed ? "success.subtle" : "canvas.subtle",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: isConfirmed ? "success.emphasis" : "accent.muted",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                      }
                    }}>
                      {/* Header với thông tin học sinh */}
                      <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 1
                      }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            mb: 1
                          }}>
                         
                            <Box
                              onClick={() => {
                                setHocSinhEditing({ id: data.ts_hocsinh_id });
                                setIsShowHocSinhProfile(true);
                              }}
                              sx={{ cursor: "pointer" }}
                            >
                              <HocSinhShortInfoWithClass data={data} />
                            </Box>
                          </Box>                         
                        </Box>

                        <Button
                          text={isConfirmed ? "Đã xác nhận" : "Xác nhận"}
                          variant={isConfirmed ? "default" : "primary"}
                          size="small"
                          disabled={isConfirmed}
                          onClick={() => {
                            if (!isConfirmed) {
                              setSelectedDanThuocHocSinh(data);
                              setIsShowDanThuocModal(true);
                            }
                          }}
                          sx={isConfirmed ? {
                            backgroundColor: "success.subtle",
                            color: "success.fg",
                            cursor: "not-allowed"
                          } : {}}
                        />
                      </Box>

                      {/* Thời gian dặn thuốc */}
                      <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                        p: 2,
                        backgroundColor: "canvas.default",
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "border.muted"
                      }}>
                        <PrimerText sx={{ fontSize: 1, color: "fg.muted" }}>
                          Thời gian:
                        </PrimerText>
                        <PrimerText sx={{ fontSize: 1, fontWeight: 600 }}>
                          {moment(data.tu_ngay).format("DD/MM/YYYY")}
                        </PrimerText>
                        <PrimerText sx={{ fontSize: 1, color: "fg.muted" }}>
                          đến
                        </PrimerText>
                        <PrimerText sx={{ fontSize: 1, fontWeight: 600 }}>
                          {moment(data.den_ngay).format("DD/MM/YYYY")}
                        </PrimerText>
                      </Box>

                      {/* Chi tiết thuốc */}
                      <Box sx={{ mb: 1 }}>                       
                        {data.chiTiets && data.chiTiets.length > 0 ? (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            {data.chiTiets.slice(0, 3).map((chiTiet, idx) => (
                              <Box key={idx} sx={{
                                p: 2,
                                border: "1px solid",
                                borderColor: "border.muted",
                                borderRadius: 1,
                                backgroundColor: "canvas.default"
                              }}>                             
                                <PrimerText sx={{
                                  fontSize: 1,
                                  fontWeight: 600,
                                  color: "accent.fg",
                                  mb: 0.5
                                }}>
                                  <TextWithEllipse text={chiTiet.ten_thuoc} lineNumber={1} />
                                </PrimerText>
                                <PrimerText sx={{
                                  fontSize: 0,
                                  color: "fg.muted"
                                }}>
                                  <TextWithEllipse text={chiTiet.cachdung_lieuluong} lineNumber={2} />
                                </PrimerText>
                              </Box>
                            ))}
                            {data.chiTiets.length > 3 && (
                              <PrimerText sx={{
                                fontSize: 0,
                                color: "fg.muted",
                                fontStyle: "italic",
                                textAlign: "center",
                                mt: 1
                              }}>
                                +{data.chiTiets.length - 3} thuốc khác...
                              </PrimerText>
                            )}
                          </Box>
                        ) : (
                          <PrimerText sx={{
                            fontSize: 1,
                            color: "fg.muted",
                            fontStyle: "italic"
                          }}>
                            Chưa có chi tiết thuốc
                          </PrimerText>
                        )}
                      </Box>

                      {/* Hình ảnh */}
                      {images.length > 0 && (
                        <Box>                       
                          <Box sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1
                          }}>
                            {images.filter((imageItem: any) => imageItem.url_hinh_anh).map((imageItem: any, idx: number) => (
                              <Box key={idx}>
                                <Box
                                  onClick={() => setPreviewImage(imageItem.url_hinh_anh)}
                                  sx={{
                                    cursor: "pointer",
                                    border: "1px solid",
                                    borderColor: "border.muted",
                                    borderRadius: 1,
                                    overflow: "hidden",
                                    "&:hover": {
                                      borderColor: "accent.muted"
                                    }
                                  }}
                                >
                                  <img
                                    src={imageItem.url_hinh_anh}
                                    alt='Medicine Image'
                                    style={{
                                      height: "60px",
                                      width: "60px",
                                      objectFit: "cover"
                                    }}
                                  />
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {isShowModal && (
        <LichSuXuongPhongYTeModal
          id={selectedId}
          onClose={() => {
            handleReloadAsync();
            setIsShowModal(false);
          }}
          onSuccess={() => {
            handleReloadAsync();
            setIsShowModal(false);
          }}
        />
      )}

      {/* Modal xác nhận dặn thuốc */}
      {isShowDanThuocModal && selectedDanThuocHocSinh && (
        <LichSuXuongPhongYTeModal
          id={0} // Tạo mới
          onClose={() => {
            setIsShowDanThuocModal(false);
            setSelectedDanThuocHocSinh(null);
          }}
          onSuccess={() => {
            handleReloadAsync();
            handleReloadDanThuocAsync();
            setIsShowDanThuocModal(false);
            setSelectedDanThuocHocSinh(null);
          }}
          // Truyền thông tin học sinh để pre-fill
          prefilledData={{
            ts_hocsinh_id: selectedDanThuocHocSinh.ts_hocsinh_id,
            ho_ten: selectedDanThuocHocSinh.ho_ten,
            ma_hs: selectedDanThuocHocSinh.ma_hs,
            ten_lop: selectedDanThuocHocSinh.ten_lop,
            ten_khoi: selectedDanThuocHocSinh.ten_khoi,
            ten_he: selectedDanThuocHocSinh.ten_he,
            danThuocInfo: selectedDanThuocHocSinh
          }}
        />
      )}

      {/* Modal HocSinhProfile */}
      {isShowHocSinhProfile && canEdit && (
        <HocSinhProfile
          id={hocSinhEditing.id}
          onClose={() => {
            setIsShowHocSinhProfile(false);
          }}
          onSuccess={() => {
            handleReloadAsync();
            handleReloadDanThuocAsync();
            // setIsShowHocSinhProfile(false);
          }}
        />
      )}

      {/* Modal preview ảnh */}
      {previewImage && (
        <Dialog
          isOpen={!!previewImage}
          onDismiss={() => setPreviewImage(null)}
          aria-labelledby="preview-image-title"
        >
          <Dialog.Header id="preview-image-title">
            Xem ảnh
          </Dialog.Header>
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <img
              src={previewImage}
              alt="Preview"
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain'
              }}
            />
          </Box>
        </Dialog>
      )}
    </Box>
  );
};

export default LichSuXuongPhongYTePage;
